export type AssetOwner = 'global' | 'game';
export type AssetFormat = 'svg' | 'png' | 'avif' | 'webp' | 'woff2';

export interface AssetVariant {
  src: string;
  width: number;
  height: number;
  format: AssetFormat;
  media?: string;
}

export interface AssetDefinition {
  key: string;
  purpose: string;
  owner: AssetOwner;
  required: boolean;
  src: string;
  width: number;
  height: number;
  aspectRatio: `${number}:${number}`;
  format: AssetFormat;
  alpha: boolean;
  alt: 'decorative' | 'meaningful' | 'brand-adjacent';
  variants?: readonly AssetVariant[];
}

/**
 * The only global public-asset paths application components should consume.
 * Real art replaces these stable destinations without layout changes.
 */
export const ASSETS = {
  brandMark: {
    key: 'brand.mark.square',
    purpose: 'Square mark beside the selectable-text brand name',
    owner: 'global',
    required: true,
    // Final art (docs/ART_ASSETS.md §3); its placeholder SVG is gone.
    src: '/assets/shared/brand/logo-mark.png',
    width: 512,
    height: 512,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'brand-adjacent',
  },
  brandWordmark: {
    key: 'brand.wordmark.horizontal',
    purpose: 'Optional horizontal brand lockup for wide placements',
    owner: 'global',
    required: false,
    // Final art (docs/ART_ASSETS.md §3); its placeholder SVG is gone.
    src: '/assets/shared/brand/logo-full.png',
    width: 1400,
    height: 360,
    aspectRatio: '35:9',
    format: 'png',
    alpha: true,
    alt: 'brand-adjacent',
  },
  brandMonochrome: {
    key: 'brand.mark.monochrome',
    purpose: 'Single-color mark for constrained placements',
    owner: 'global',
    required: false,
    src: '/assets/placeholders/brand-mark-monochrome.svg',
    width: 512,
    height: 512,
    aspectRatio: '1:1',
    format: 'svg',
    alpha: true,
    alt: 'brand-adjacent',
  },
  favicon16: {
    key: 'brand.favicon.16',
    purpose: '16px browser favicon',
    owner: 'global',
    required: true,
    src: '/icons/favicon-16.png',
    width: 16,
    height: 16,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  favicon32: {
    key: 'brand.favicon.32',
    purpose: '32px browser favicon',
    owner: 'global',
    required: true,
    src: '/icons/favicon-32.png',
    width: 32,
    height: 32,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  appleTouchIcon: {
    key: 'brand.apple-touch-icon',
    purpose: 'iOS home-screen icon',
    owner: 'global',
    required: true,
    src: '/icons/apple-touch-icon.png',
    width: 180,
    height: 180,
    aspectRatio: '1:1',
    format: 'png',
    alpha: false,
    alt: 'decorative',
  },
  pwaIcon192: {
    key: 'brand.pwa.192',
    purpose: 'PWA install icon',
    owner: 'global',
    required: true,
    src: '/icons/icon-192.png',
    width: 192,
    height: 192,
    aspectRatio: '1:1',
    format: 'png',
    alpha: false,
    alt: 'decorative',
  },
  pwaIcon512: {
    key: 'brand.pwa.512',
    purpose: 'Large PWA install icon',
    owner: 'global',
    required: true,
    src: '/icons/icon-512.png',
    width: 512,
    height: 512,
    aspectRatio: '1:1',
    format: 'png',
    alpha: false,
    alt: 'decorative',
  },
  pwaMaskable512: {
    key: 'brand.pwa.maskable.512',
    purpose: 'Maskable PWA icon with central safe zone',
    owner: 'global',
    required: true,
    src: '/icons/icon-maskable-512.png',
    width: 512,
    height: 512,
    aspectRatio: '1:1',
    format: 'png',
    alpha: false,
    alt: 'decorative',
  },
  loadingSplash: {
    key: 'illustration.loading-splash',
    purpose: 'Loading and public-entry illustration',
    owner: 'global',
    required: false,
    // Final art (docs/ART_ASSETS.md §3, "loading/open-book"); its
    // placeholder SVG is gone.
    src: '/assets/shared/loading/open-book.webp',
    width: 1200,
    height: 800,
    aspectRatio: '3:2',
    format: 'webp',
    alpha: false,
    alt: 'decorative',
  },
  appBackground: {
    key: 'background.default',
    purpose: 'Decorative application-shell background',
    owner: 'global',
    required: true,
    // Final art (docs/ART_ASSETS.md §3, "backgrounds/page-forest-*"); its
    // placeholder SVG is gone. The portrait mobile recomposition is a real
    // separate composition (docs/ART_ASSETS.md §12), consumed by a media
    // query in `styles/index.css`.
    src: '/assets/shared/backgrounds/page-forest-desktop.webp',
    width: 1920,
    height: 1080,
    aspectRatio: '16:9',
    format: 'webp',
    alpha: false,
    alt: 'decorative',
    variants: [
      {
        src: '/assets/shared/backgrounds/page-forest-mobile.webp',
        width: 1080,
        height: 1920,
        format: 'webp',
        media: '(max-width: 639px)',
      },
    ],
  },
  gameCatalogDefault: {
    key: 'game.catalog.default',
    purpose: 'Fallback game-catalog card artwork',
    owner: 'global',
    required: true,
    src: '/assets/placeholders/game-card-16x9.svg',
    width: 640,
    height: 360,
    aspectRatio: '16:9',
    format: 'svg',
    alpha: false,
    alt: 'decorative',
  },
  gameHeroDefault: {
    key: 'game.hero.default',
    purpose: 'Fallback game-session hero/background',
    owner: 'global',
    required: false,
    src: '/assets/placeholders/game-hero-16x9.svg',
    width: 1920,
    height: 1080,
    aspectRatio: '16:9',
    format: 'svg',
    alpha: false,
    alt: 'decorative',
  },
  gameProfileDefault: {
    key: 'game.profile-card.default',
    purpose: 'Fallback header artwork for a game card on the profile Gameplay section',
    owner: 'global',
    required: false,
    src: '/assets/placeholders/game-profile-card-8x3.svg',
    width: 800,
    height: 300,
    aspectRatio: '8:3',
    format: 'svg',
    alpha: false,
    alt: 'decorative',
  },

  // ---------------------------------------------------------------------------
  // Public profile revamp (APP_SPEC §13.2–§13.3). Three independently
  // selectable structural cosmetic slots plus the stable destinations the
  // profile UI resolves its decorative artwork from. Real art swaps in by
  // changing the `src` here only — same pattern as `brandMark` / `appBackground`
  // (docs/ART_ASSETS.md §30, docs/UI_GUIDELINES.md §11). Per-stat icon sets and
  // per-game card art are centralized in `features/profile/profile-assets.ts`
  // and `games/registry.ts` respectively, not scattered through JSX.
  // ---------------------------------------------------------------------------
  profileHeroBackground: {
    key: 'profile.hero.background',
    purpose: 'Replaceable decorative background filling the unified profile hero',
    owner: 'global',
    required: false,
    src: '/assets/profile/placeholders/profile-hero-background.svg',
    width: 1600,
    height: 400,
    aspectRatio: '4:1',
    format: 'svg',
    alpha: false,
    alt: 'decorative',
  },
  profileAvatarFrame: {
    key: 'profile.avatar.frame',
    purpose: 'Decorative frame layer drawn over (never flattened into) the player avatar',
    owner: 'global',
    required: false,
    src: '/assets/profile/placeholders/profile-avatar-frame.svg',
    width: 220,
    height: 220,
    aspectRatio: '1:1',
    format: 'svg',
    alpha: true,
    alt: 'decorative',
  },
  profileSideDecoration: {
    key: 'profile.side.decoration',
    purpose:
      'Decorative image pinned to the far-right of the profile hero (its own cosmetic layer)',
    owner: 'global',
    required: false,
    src: '/assets/profile/placeholders/profile-side-decoration.svg',
    width: 320,
    height: 480,
    aspectRatio: '2:3',
    format: 'svg',
    alpha: true,
    alt: 'decorative',
  },
  editorialSquare: {
    key: 'illustration.editorial.square',
    purpose: 'Dictionary-style editorial illustration fallback',
    owner: 'global',
    required: false,
    src: '/assets/placeholders/editorial-square.svg',
    width: 512,
    height: 512,
    aspectRatio: '1:1',
    format: 'svg',
    alpha: false,
    alt: 'decorative',
  },
  ornamentalCorners: {
    key: 'ornament.corners',
    purpose: 'Optional transparent corner flourishes for large frames',
    owner: 'global',
    required: false,
    src: '/assets/placeholders/ornament-corners.svg',
    width: 256,
    height: 256,
    aspectRatio: '1:1',
    format: 'svg',
    alpha: true,
    alt: 'decorative',
  },
  ornamentalDivider: {
    key: 'ornament.divider',
    purpose: 'Optional section divider',
    owner: 'global',
    required: false,
    src: '/assets/placeholders/ornament-divider.svg',
    width: 800,
    height: 80,
    aspectRatio: '10:1',
    format: 'svg',
    alpha: true,
    alt: 'decorative',
  },
  emptyState: {
    key: 'illustration.empty-state',
    purpose: 'Optional global empty-state illustration',
    owner: 'global',
    required: false,
    src: '/assets/placeholders/empty-state-4x3.svg',
    width: 800,
    height: 600,
    aspectRatio: '4:3',
    format: 'svg',
    alpha: true,
    alt: 'decorative',
  },
  socialShare: {
    key: 'brand.social-share',
    purpose: 'Default link-preview image',
    owner: 'global',
    required: false,
    src: '/assets/placeholders/social-share.svg',
    width: 1200,
    height: 630,
    aspectRatio: '40:21',
    format: 'svg',
    alpha: false,
    alt: 'decorative',
  },

  // ---------------------------------------------------------------------------
  // Shared pixel-art UI kit (docs/ART_ASSETS.md §21). These are the stable
  // destinations the shared `Button` / `Card` / `NineSlice` / `LoadingState`
  // components resolve their decorative art from — brass-edged 9-slice frames,
  // parchment textures, and ornament glyphs. Swapping the final art in is an
  // edit here only, exactly like the navigation icons and game catalog art
  // (docs/ART_ASSETS.md §30, docs/UI_GUIDELINES.md §11).
  // ---------------------------------------------------------------------------
  uiButtonPrimary: {
    key: 'ui.button.primary',
    purpose: '9-slice face for the decorated primary button (rest state)',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/buttons/primary.9.png',
    width: 128,
    height: 64,
    aspectRatio: '2:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiButtonPrimaryHover: {
    key: 'ui.button.primary-hover',
    purpose: '9-slice face for the decorated primary button (hover state)',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/buttons/primary-hover.9.png',
    width: 128,
    height: 64,
    aspectRatio: '2:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiButtonPrimaryPressed: {
    key: 'ui.button.primary-pressed',
    purpose: '9-slice face for the decorated primary button (pressed state)',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/buttons/primary-pressed.9.png',
    width: 128,
    height: 64,
    aspectRatio: '2:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiButtonPrimaryDisabled: {
    key: 'ui.button.primary-disabled',
    purpose: '9-slice face for the decorated primary button (disabled state)',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/buttons/primary-disabled.9.png',
    width: 128,
    height: 64,
    aspectRatio: '2:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiButtonSecondary: {
    key: 'ui.button.secondary',
    purpose: '9-slice face for the decorated secondary button',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/buttons/secondary.9.png',
    width: 128,
    height: 64,
    aspectRatio: '2:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiFramePanelParchment: {
    key: 'ui.frame.panel-parchment',
    purpose: 'Filled brass-edged parchment panel frame for hero regions',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/frames/panel-parchment.9.png',
    width: 96,
    height: 96,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiFramePanelGold: {
    key: 'ui.frame.panel-gold',
    purpose: 'Hollow brass panel frame drawn over an existing surface',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/frames/panel-gold.9.png',
    width: 96,
    height: 96,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiFramePanelDark: {
    key: 'ui.frame.panel-dark',
    purpose: 'Hollow dark frame for game rails and dark cards',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/frames/panel-dark.9.png',
    width: 96,
    height: 96,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiFramePortrait: {
    key: 'ui.frame.portrait',
    purpose: 'Hollow brass portrait frame for large avatars',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/frames/portrait.9.png',
    width: 96,
    height: 96,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiFrameItemSlot: {
    key: 'ui.frame.item-slot',
    purpose: 'Hollow slot frame for selectable option tiles',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/frames/item-slot.9.png',
    width: 96,
    height: 96,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiFrameItemSlotSelected: {
    key: 'ui.frame.item-slot-selected',
    purpose: 'Selected-state glow slot frame for option tiles',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/frames/item-slot-selected.9.png',
    width: 96,
    height: 96,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  uiBannerParchmentWide: {
    key: 'ui.banner.parchment-wide',
    purpose: 'Filled parchment banner backing for wide title bars',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/banners/parchment-wide.9.png',
    width: 128,
    height: 64,
    aspectRatio: '2:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  textureParchment: {
    key: 'texture.parchment',
    purpose: 'Subtle parchment reading-surface texture for cards and dialogs',
    owner: 'global',
    required: false,
    src: '/assets/shared/backgrounds/parchment.webp',
    width: 512,
    height: 512,
    aspectRatio: '1:1',
    format: 'webp',
    alpha: false,
    alt: 'decorative',
  },
  texturePanelVignette: {
    key: 'texture.panel-vignette',
    purpose: 'Soft inner vignette for dark framed regions',
    owner: 'global',
    required: false,
    src: '/assets/shared/backgrounds/panel-vignette.webp',
    width: 512,
    height: 512,
    aspectRatio: '1:1',
    format: 'webp',
    alpha: true,
    alt: 'decorative',
  },
  textureGreen: {
    key: 'texture.green',
    purpose: 'Dark emerald texture for the application header and footer',
    owner: 'global',
    required: false,
    src: '/assets/shared/backgrounds/green-texture.webp',
    width: 512,
    height: 512,
    aspectRatio: '1:1',
    format: 'webp',
    alpha: false,
    alt: 'decorative',
  },
  decorFlourish: {
    key: 'decor.flourish-gold',
    purpose: 'Small brass flourish accent above headings and hero copy',
    owner: 'global',
    required: false,
    src: '/assets/shared/brand/flourish-gold.svg',
    width: 64,
    height: 64,
    aspectRatio: '1:1',
    format: 'svg',
    alpha: true,
    alt: 'decorative',
  },
  decorDivider: {
    key: 'decor.divider-horizontal',
    purpose: 'Brass divider glyph for section rules',
    owner: 'global',
    required: false,
    src: '/assets/shared/brand/divider-horizontal.svg',
    width: 64,
    height: 64,
    aspectRatio: '1:1',
    format: 'svg',
    alpha: true,
    alt: 'decorative',
  },
  decorCorner: {
    key: 'decor.gold-corner',
    purpose: 'Optional brass corner ornament for large shell frames',
    owner: 'global',
    required: false,
    src: '/assets/shared/decor/gold-corner.svg',
    width: 64,
    height: 64,
    aspectRatio: '1:1',
    format: 'svg',
    alpha: true,
    alt: 'decorative',
  },
  decorRune: {
    key: 'decor.rune-small',
    purpose: 'Small rune accent for status and label emphasis',
    owner: 'global',
    required: false,
    src: '/assets/shared/decor/rune-small.svg',
    width: 64,
    height: 64,
    aspectRatio: '1:1',
    format: 'svg',
    alpha: true,
    alt: 'decorative',
  },
  illustrationBooksCandle: {
    key: 'illustration.books-candle',
    purpose: 'Books-and-candle illustration for empty states and hero panels',
    owner: 'global',
    required: false,
    src: '/assets/shared/decor/dictionary-books-candle.webp',
    width: 1024,
    height: 1024,
    aspectRatio: '1:1',
    format: 'webp',
    alpha: false,
    alt: 'decorative',
  },
  loadingGlow: {
    key: 'loading.magical-glow',
    purpose: 'Soft magical glow behind the loading rune',
    owner: 'global',
    required: false,
    src: '/assets/shared/loading/magical-glow.webp',
    width: 512,
    height: 512,
    aspectRatio: '1:1',
    format: 'webp',
    alpha: true,
    alt: 'decorative',
  },
  loadingRune: {
    key: 'loading.rune',
    purpose: 'Rotating rune mark for the shared loading indicator',
    owner: 'global',
    required: false,
    src: '/assets/shared/loading/rune-1.svg',
    width: 64,
    height: 64,
    aspectRatio: '1:1',
    format: 'svg',
    alpha: true,
    alt: 'decorative',
  },
  fxSparkle: {
    key: 'fx.sparkle-large',
    purpose: 'Restrained sparkle burst for victory moments',
    owner: 'global',
    required: false,
    src: '/assets/shared/fx/sparkle-large.png',
    width: 128,
    height: 128,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },

  // ---------------------------------------------------------------------------
  // Games page (APP_SPEC §17, §18; docs/UI_GUIDELINES.md §8). The catalog's own
  // decorated button face, session-mode glyphs, and header backdrop — distinct
  // from the shared UI kit above because this page's redesign calls for a
  // brighter, more ornate brass/emerald treatment than the rest of the app.
  // ---------------------------------------------------------------------------
  uiButtonGamePrimary: {
    key: 'ui.button.game-primary',
    purpose: 'Blank brass-edged 9-slice face for the Games page action buttons',
    owner: 'global',
    required: false,
    src: '/assets/shared/ui/buttons/game-primary.9.png',
    width: 543,
    height: 181,
    aspectRatio: '3:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  gamesHeroBackground: {
    key: 'games.header.background',
    purpose: 'Decorative map illustration bleeding into the Games page header',
    owner: 'global',
    required: false,
    src: '/assets/shared/backgrounds/games-hero.webp',
    width: 1284,
    height: 428,
    aspectRatio: '3:1',
    format: 'webp',
    alpha: false,
    alt: 'decorative',
  },
  gameModeSolo: {
    key: 'games.mode.solo',
    purpose: 'Helmet glyph for the Solo session mode',
    owner: 'global',
    required: false,
    src: '/assets/shared/icons/modes/solo.png',
    width: 256,
    height: 256,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  gameModeVersus: {
    key: 'games.mode.versus',
    purpose: 'Crossed-swords glyph for the 1v1/Challenge session mode',
    owner: 'global',
    required: false,
    src: '/assets/shared/icons/modes/versus.png',
    width: 256,
    height: 256,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  gameModeMultiplayer: {
    key: 'games.mode.multiplayer',
    purpose: 'Three-person medallion glyph for the Multiplayer/Host session mode',
    owner: 'global',
    required: false,
    src: '/assets/shared/icons/modes/multiplayer.png',
    width: 256,
    height: 256,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  gameModeContinue: {
    key: 'games.mode.continue',
    purpose: 'Torn-flag glyph for the Continue Playing section heading',
    owner: 'global',
    required: false,
    src: '/assets/shared/icons/modes/continue.png',
    width: 256,
    height: 256,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },

  // The catalog card's metadata glyphs — one per *kind* of derived chip
  // (`lib/game-meta.ts`'s `GameMetaKind`), never per game. Deliberately a
  // separate, quieter set from the `games.mode.*` launch glyphs above: these
  // annotate a line of small type, so they are single-weight marks rather than
  // illustrated medallions.
  //
  // All three masters carry a large transparent margin — the drawn symbol fills
  // only ~35-45% of the canvas height — so the rendered box must be far larger
  // than the mark is meant to appear. The card cancels the surplus with a
  // negative block margin rather than letting it inflate the metadata line; see
  // `GameCatalogCard`'s `META_ICON_CLASS`.
  gameMetaSolo: {
    key: 'games.meta.solo',
    purpose: 'Single-figure glyph for a catalog card\'s "Solo" metadata chip',
    owner: 'global',
    required: false,
    src: '/assets/shared/icons/meta/solo.png',
    width: 128,
    height: 128,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  gameMetaPlayers: {
    key: 'games.meta.players',
    purpose: "Group glyph for a catalog card's player-count metadata chip",
    owner: 'global',
    required: false,
    src: '/assets/shared/icons/meta/players.png',
    width: 128,
    height: 128,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
  gameMetaRealtime: {
    key: 'games.meta.realtime',
    purpose: 'Hourglass glyph for a catalog card\'s "Real-time" metadata chip',
    owner: 'global',
    required: false,
    src: '/assets/shared/icons/meta/realtime.png',
    width: 128,
    height: 128,
    aspectRatio: '1:1',
    format: 'png',
    alpha: true,
    alt: 'decorative',
  },
} as const satisfies Record<string, AssetDefinition>;

export type AssetName = keyof typeof ASSETS;

export function assetUrl(name: AssetName): string {
  return ASSETS[name].src;
}

/**
 * The `src` of the first registered variant whose `media` query matches
 * `query`, or `undefined` when the asset has no such variant. Used for the
 * responsive background recompositions (docs/ART_ASSETS.md §12).
 */
export function assetVariantUrl(name: AssetName, media: string): string | undefined {
  const asset: AssetDefinition = ASSETS[name];
  return asset.variants?.find((variant) => variant.media === media)?.src;
}

export const ASSET_LIST = Object.values(ASSETS);

/**
 * A game's catalog-card artwork: the 1x file plus, where a larger rendition
 * exists, the `srcSet` that lets the browser pick by rendered width and DPR
 * (docs/UI_GUIDELINES.md §12's 1x/2x pair).
 */
export interface GameCatalogArt {
  src: string;
  srcSet?: string;
  /**
   * The rendition the *catalog banner* draws, when it should differ from
   * `src`. The banner crops far harder than the plain card image does (see
   * `bannerSize`), so it needs the larger rendition to stay a downscale.
   */
  bannerSrc?: string;
  /**
   * CSS `background-size` / `background-position` for the **catalog** banner
   * crop, which is a genuinely different problem from the profile card's
   * (`GameProfileArt.bannerPosition`) and must not be assumed to share its
   * value. The profile banner is roughly 8:3; the catalog banner is a much
   * wider, shallower band (~3.8:1 normal, ~2:1 on the wide final card). A plain
   * `cover` fit in that band is width-constrained, which leaves *no* horizontal
   * slack — so a master whose subject sits off to one side cannot be framed by
   * an anchor alone, and needs a real zoom to bring the subject up.
   *
   * These are presentation-only values: they change which part of a painting is
   * visible and nothing else. Defaults (`cover`, centre) apply to any game that
   * declares neither.
   */
  bannerSize?: string;
  bannerPosition?: string;
}

/** A catalog banner's resolved image and crop. */
export interface CatalogBannerArt {
  image: string;
  size: string;
  position: string;
}

/**
 * Resolves the catalog banner's image and crop, falling back to the shared
 * placeholder and a plain centred `cover` fit for a game that declares
 * neither. Like `resolveGameCatalogArt`, the caller passes the game's own
 * declaration in rather than this module holding a `gameId` table (CLAUDE.md
 * §2).
 */
export function resolveCatalogBanner(owned: GameCatalogArt | undefined): CatalogBannerArt {
  const art = resolveGameCatalogArt(owned);
  return {
    image: owned?.bannerSrc ?? art.src,
    size: owned?.bannerSize ?? 'cover',
    position: owned?.bannerPosition ?? 'center',
  };
}

/**
 * Resolves a game's catalog card artwork, falling back to the shared default
 * when the caller has no game-owned entry (docs/UI_GUIDELINES.md §12). This
 * module intentionally holds no per-`gameId` lookup table: a registered
 * `gameId` string literal may only appear inside `apps/web/src/games/**` or
 * `packages/games/index.ts` (CLAUDE.md §2) — the actual `gameId -> art` map
 * therefore lives in `apps/web/src/games/registry.ts`'s `GAME_CATALOG_ART`,
 * and callers (e.g. `GameCard.tsx`) pass the already-resolved value in here
 * only for the fallback.
 */
export function resolveGameCatalogArt(owned: GameCatalogArt | undefined): GameCatalogArt {
  return owned ?? { src: ASSETS.gameCatalogDefault.src };
}

/**
 * A game's artwork for its card in the profile Gameplay section (APP_SPEC
 * §13.3): a wide header background plus a square icon.
 *
 * The header background is deliberately not a game-owned slot of its own —
 * it is the game's **catalog card image** (`GAME_CATALOG_ART`) reused, so a
 * game is recognisable by the same picture everywhere and a new game needs
 * zero profile-background wiring. It is rendered essentially unfiltered —
 * the source palette is the point — with only a left-weighted gradient under
 * the emblem and title for legibility. The game-owned profile slots
 * (`GAME_PROFILE_ART` in `apps/web/src/games/registry.ts`) are the square
 * `icon` and the banner crop; callers pass the catalog art and that
 * declaration in here for the shared fallbacks, so a game added before
 * either arrives still renders.
 */
export interface GameProfileArt {
  cardBackground: string;
  icon: string;
  /**
   * CSS `background-size` / `background-position` for the banner crop. The
   * banner is a wide band cut out of a 16:9 illustration, so *which* band is
   * shown is a property of the artwork, not of the profile: a game whose
   * subject sits low needs a different vertical anchor than one whose subject
   * sits high. Games declare it beside their icon in
   * `apps/web/src/games/registry.ts`; the defaults here (`cover`, centre) are
   * what an unconfigured game gets, and are always a plain cover fit — the
   * banner is never zoomed past `cover` by default.
   */
  bannerSize: string;
  bannerPosition: string;
}

/** A game's own profile-art declaration (`GAME_PROFILE_ART` in the web game registry). */
export interface OwnedGameProfileArt {
  icon: string;
  bannerSize?: string;
  bannerPosition?: string;
}

export function resolveGameProfileArt(
  catalogArt: GameCatalogArt | undefined,
  owned: OwnedGameProfileArt | undefined,
): GameProfileArt {
  return {
    cardBackground: catalogArt?.src ?? ASSETS.gameProfileDefault.src,
    icon: owned?.icon ?? ASSETS.editorialSquare.src,
    bannerSize: owned?.bannerSize ?? 'cover',
    bannerPosition: owned?.bannerPosition ?? 'center',
  };
}
